import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    general: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Pre-fill data from ad posting if available
  useEffect(() => {
    // Get ad data from navigation state
    if (location.state && location.state.adData) {
      const data = location.state.adData;
      setFormData(prev => ({
        ...prev,
        fullName: data.contactName || '',
        email: data.contactEmail || '',
        phone: data.contactPhone || ''
      }));
    }

    // Check if we should show sign-up form
    if (location.state && location.state.isSignUp) {
      setIsSignUp(true);
    }
  }, [location.state]);

  const generateUsernameSuggestions = (baseUsername: string): string[] => {
    const suggestions: string[] = [];
    const randomNum = () => Math.floor(Math.random() * 999);
    
    suggestions.push(`${baseUsername}${randomNum()}`);
    suggestions.push(`${baseUsername}_${randomNum()}`);
    suggestions.push(`${baseUsername}.${randomNum()}`);
    suggestions.push(`user_${baseUsername}${randomNum()}`);
    suggestions.push(`${baseUsername}${new Date().getFullYear()}`);
    
    return suggestions;
  };

  const checkUsernameAvailability = async (username: string) => {
    if (!username || username.length < 3) return;
    
    setCheckingUsername(true);
    setUsernameSuggestions([]);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', username.toLowerCase())
        .single();

      if (data) {
        setErrors(prev => ({ 
          ...prev, 
          username: 'This username is already taken' 
        }));
        setUsernameSuggestions(generateUsernameSuggestions(username));
      } else if (error && error.code === 'PGRST116') {
        // Username is available
        setErrors(prev => ({ ...prev, username: '' }));
      }
    } catch (err) {
      console.error('Error checking username:', err);
    } finally {
      setCheckingUsername(false);
    }
  };

  const checkEmailExists = async (email: string) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    
    try {
      const { data } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', email.toLowerCase())
        .single();

      if (data) {
        setErrors(prev => ({ 
          ...prev, 
          email: 'This email is already registered. Please sign in instead.' 
        }));
        return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  };

  const validatePassword = (password: string): boolean => {
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasLetter = /[a-zA-Z]/.test(password);
    
    if (password.length < 8) {
      setErrors(prev => ({ 
        ...prev, 
        password: 'Password must be at least 8 characters long' 
      }));
      return false;
    }
    
    if (!hasSpecialChar) {
      setErrors(prev => ({ 
        ...prev, 
        password: 'Password must contain at least one special character (!@#$%^&*...)' 
      }));
      return false;
    }
    
    if (!hasNumber || !hasLetter) {
      setErrors(prev => ({ 
        ...prev, 
        password: 'Password must contain both letters and numbers' 
      }));
      return false;
    }
    
    setErrors(prev => ({ ...prev, password: '' }));
    return true;
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\d\s\-\+\(\)]{10,}$/;
    if (!phoneRegex.test(phone)) {
      setErrors(prev => ({ 
        ...prev, 
        phone: 'Please enter a valid phone number (at least 10 digits)' 
      }));
      return false;
    }
    setErrors(prev => ({ ...prev, phone: '' }));
    return true;
  };

  const validateForm = async () => {
    const newErrors = {
      username: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      general: ''
    };
    let isValid = true;

    if (isSignUp) {
      // Username validation
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
        isValid = false;
      } else if (formData.username.length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
        isValid = false;
      } else if (!/^[a-zA-Z0-9_.-]+$/.test(formData.username)) {
        newErrors.username = 'Username can only contain letters, numbers, dots, dashes, and underscores';
        isValid = false;
      }

      // Full name validation
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
        isValid = false;
      }

      // Phone validation
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
        isValid = false;
      } else if (!validatePhone(formData.phone)) {
        isValid = false;
      }

      // Password validation
      if (!validatePassword(formData.password)) {
        isValid = false;
      }

      // Confirm password validation
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
        isValid = false;
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
        isValid = false;
      }
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      isValid = false;
    }

    // Password validation for sign in
    if (!isSignUp && !formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!await validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({ username: '', email: '', phone: '', password: '', confirmPassword: '', fullName: '', general: '' });

    try {
      if (isSignUp) {
        // Check if email already exists
        const emailExists = await checkEmailExists(formData.email);
        if (emailExists) {
          setIsLoading(false);
          return;
        }

        // Check username one more time before signup
        const { data: existingUser } = await supabase
          .from('profiles')
          .select('username')
          .eq('username', formData.username.toLowerCase())
          .single();

        if (existingUser) {
          setErrors(prev => ({ 
            ...prev, 
            username: 'This username is already taken' 
          }));
          setUsernameSuggestions(generateUsernameSuggestions(formData.username));
          setIsLoading(false);
          return;
        }

        // Sign up with Supabase Auth
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              full_name: formData.fullName,
              username: formData.username.toLowerCase(),
              phone: formData.phone
            }
          }
        });

        if (authError) {
          setErrors(prev => ({ ...prev, general: authError.message }));
          setIsLoading(false);
          return;
        }

        if (authData.user) {
          // Create profile with username and phone
          const { error: profileError } = await supabase
            .from('profiles')
            .insert({
              id: authData.user.id,
              email: formData.email.toLowerCase(),
              full_name: formData.fullName,
              username: formData.username.toLowerCase(),
              phone: formData.phone,
              status: 'active'
            });

          if (profileError) {
            console.error('Profile creation error:', profileError);
          }

          // If there's ad data, save it now
          const adData = location.state?.adData;
          if (adData) {
            const { error: adError } = await supabase
              .from('ads')
              .insert({
                user_id: authData.user.id,
                title: adData.title,
                description: adData.description,
                category: adData.category,
                subcategory: adData.subcategory,
                price: adData.price,
                location: adData.location,
                images: adData.images || [],
                status: 'active',
                expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                ...adData.additionalFields
              });

            if (!adError) {
              navigate('/ad-success', { 
                state: { 
                  adData: { ...adData, id: authData.user.id },
                  fromSignup: true 
                } 
              });
              return;
            }
          }

          setErrors(prev => ({ 
            ...prev, 
            general: 'Account created! Please check your email to verify your account.' 
          }));
          
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      } else {
        // Sign in
        const { data, error } = await signIn(formData.email, formData.password);
        
        if (error) {
          // Provide more helpful error messages
          let errorMessage = error.message;
          
          if (error.message.includes('Email not confirmed')) {
            errorMessage = 'Please verify your email address. Check your inbox for a confirmation email and click the verification link. Don\'t forget to check your spam folder!';
          } else if (error.message.includes('Invalid login credentials')) {
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          }
          
          setErrors(prev => ({ ...prev, general: errorMessage }));
          setIsLoading(false);
          return;
        }

        // Redirect to dashboard or previous page
        const from = location.state?.from || '/dashboard';
        navigate(from);
      }
    } catch (error: any) {
      setErrors(prev => ({ ...prev, general: error.message || 'An error occurred' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google'
      });
      if (error) {
        setErrors(prev => ({ ...prev, general: error.message }));
      }
    } catch (error: any) {
      setErrors(prev => ({ ...prev, general: error.message || 'An error occurred' }));
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      setResetMessage('Please enter your email address');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(resetEmail)) {
      setResetMessage('Please enter a valid email address');
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/signin`
      });

      if (error) {
        setResetMessage(error.message);
      } else {
        setResetMessage('Password reset email sent! Check your inbox and spam folder.');
      }
    } catch (error: any) {
      setResetMessage(error.message || 'An error occurred');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Sign In/Up Form */}
          <div className="bg-gray-50 p-8 rounded-lg border border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isSignUp ? 'Create Account' : 'Sign In'}
            </h1>
            <p className="text-gray-600 mb-6">
              {isSignUp 
                ? 'Join ListIt and start buying and selling today' 
                : 'Welcome back! Sign in to your account'
              }
            </p>

            {errors.general && (
              <div className={`mb-4 p-3 rounded-lg ${
                errors.general.includes('created') 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${
                  errors.general.includes('created') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {errors.general}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignUp && (
                <>
                  {/* Username Field */}
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                      Username <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="username"
                        type="text"
                        value={formData.username}
                        onChange={(e) => {
                          const value = e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, '');
                          setFormData({ ...formData, username: value });
                          setErrors({ ...errors, username: '' });
                          setUsernameSuggestions([]);
                        }}
                        onBlur={() => checkUsernameAvailability(formData.username)}
                        placeholder="Choose a unique username"
                        className={errors.username ? 'border-red-500' : ''}
                      />
                      {checkingUsername && (
                        <div className="absolute right-3 top-3">
                          <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                        </div>
                      )}
                    </div>
                    {errors.username && (
                      <p className="text-red-500 text-xs mt-1">{errors.username}</p>
                    )}
                    {usernameSuggestions.length > 0 && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-800 font-medium mb-2">Suggested usernames:</p>
                        <div className="flex flex-wrap gap-2">
                          {usernameSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, username: suggestion });
                                setUsernameSuggestions([]);
                                setErrors({ ...errors, username: '' });
                              }}
                              className="px-3 py-1 bg-white border border-blue-300 rounded-full text-xs text-blue-700 hover:bg-blue-100 transition-colors cursor-pointer"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      3+ characters, letters, numbers, dots, dashes, underscores only
                    </p>
                  </div>

                  {/* Full Name Field */}
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => {
                        setFormData({ ...formData, fullName: e.target.value });
                        setErrors({ ...errors, fullName: '' });
                      }}
                      placeholder="Enter your full name"
                      className={errors.fullName ? 'border-red-500' : ''}
                    />
                    {errors.fullName && (
                      <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
                    )}
                  </div>
                </>
              )}

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    setErrors({ ...errors, email: '' });
                  }}
                  onBlur={() => isSignUp && checkEmailExists(formData.email)}
                  placeholder="Enter your email"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {isSignUp && (
                /* Phone Field */
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData({ ...formData, phone: e.target.value });
                      setErrors({ ...errors, phone: '' });
                    }}
                    onBlur={() => validatePhone(formData.phone)}
                    placeholder="+1 (555) 123-4567"
                    className={errors.phone ? 'border-red-500' : ''}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    This will be your unique contact number
                  </p>
                </div>
              )}

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      if (isSignUp) {
                        validatePassword(e.target.value);
                        // Check if confirm password matches
                        if (formData.confirmPassword && e.target.value !== formData.confirmPassword) {
                          setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
                        } else if (formData.confirmPassword) {
                          setErrors(prev => ({ ...prev, confirmPassword: '' }));
                        }
                      } else {
                        setErrors({ ...errors, password: '' });
                      }
                    }}
                    placeholder="Enter your password"
                    className={errors.password ? 'border-red-500' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line text-lg`}></i>
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
                {isSignUp && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-600 font-medium">Password must contain:</p>
                    <ul className="text-xs text-gray-500 space-y-0.5 ml-4">
                      <li className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                        <i className={`ri-${formData.password.length >= 8 ? 'check' : 'close'}-line mr-1`}></i>
                        At least 8 characters
                      </li>
                      <li className={/[a-zA-Z]/.test(formData.password) && /\d/.test(formData.password) ? 'text-green-600' : ''}>
                        <i className={`ri-${/[a-zA-Z]/.test(formData.password) && /\d/.test(formData.password) ? 'check' : 'close'}-line mr-1`}></i>
                        Letters and numbers
                      </li>
                      <li className={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? 'text-green-600' : ''}>
                        <i className={`ri-${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? 'check' : 'close'}-line mr-1`}></i>
                        At least one special character (!@#$%...)
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Confirm Password Field - Only for Sign Up */}
              {isSignUp && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => {
                        setFormData({ ...formData, confirmPassword: e.target.value });
                        // Check if passwords match
                        if (formData.password && e.target.value !== formData.password) {
                          setErrors({ ...errors, confirmPassword: 'Passwords do not match' });
                        } else {
                          setErrors({ ...errors, confirmPassword: '' });
                        }
                      }}
                      placeholder="Re-enter your password"
                      className={errors.confirmPassword ? 'border-red-500' : ''}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700 cursor-pointer"
                    >
                      <i className={`ri-${showConfirmPassword ? 'eye-off' : 'eye'}-line text-lg`}></i>
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                  )}
                  {formData.confirmPassword && formData.password === formData.confirmPassword && (
                    <p className="text-green-600 text-xs mt-1">
                      <i className="ri-check-line mr-1"></i>
                      Passwords match
                    </p>
                  )}
                </div>
              )}

              {!isSignUp && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                    />
                    <span className="ml-2 text-sm text-gray-700">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setResetEmail(formData.email);
                      setResetMessage('');
                    }}
                    className="text-sm text-blue-600 hover:underline cursor-pointer"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="md"
                className="w-full whitespace-nowrap"
                disabled={isLoading || checkingUsername}
              >
                {isLoading ? (isSignUp ? 'Creating Account...' : 'Signing In...') : (isSignUp ? 'Create Account' : 'Sign In')}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 text-gray-500">Or continue with</span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <i className="ri-google-fill text-lg mr-2"></i>
                  Google
                </button>
              </div>
            </div>

            <p className="mt-6 text-center text-sm text-gray-600">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            </p>
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setErrors({ username: '', email: '', phone: '', password: '', confirmPassword: '', fullName: '', general: '' });
                setUsernameSuggestions([]);
                setShowPassword(false);
                setShowConfirmPassword(false);
              }}
              className="mt-3 w-full bg-yellow-100 border-2 border-yellow-400 rounded-lg p-4 text-center text-lg font-bold text-gray-900 hover:bg-yellow-200 cursor-pointer transition-colors"
            >
              <i className="ri-user-add-line mr-2"></i>
              {isSignUp ? 'Sign In Instead' : 'Create a Free Account'}
            </button>
          </div>

          {/* Benefits Section */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Sign In?</h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-add-circle-line text-green-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Post Unlimited Ads</h3>
                  <p className="text-gray-600 text-sm">
                    Create and manage unlimited listings across all categories. Edit or delete your ads anytime.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-bookmark-line text-blue-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Save Favorites</h3>
                  <p className="text-gray-600 text-sm">
                    Bookmark interesting listings and get notified when similar items are posted.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-10 h-10 flex items-center justify-center bg-purple-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-message-3-line text-purple-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Direct Messaging</h3>
                  <p className="text-gray-600 text-sm">
                    Chat directly with buyers and sellers. Keep all your conversations in one place.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-notification-3-line text-orange-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Get Alerts</h3>
                  <p className="text-gray-600 text-sm">
                    Set up custom alerts for specific items, categories, or locations you're interested in.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-red-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Verified Account</h3>
                  <p className="text-gray-600 text-sm">
                    Build trust with verified badges. Buyers prefer dealing with verified sellers.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg mr-4 flex-shrink-0">
                  <i className="ri-line-chart-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Track Performance</h3>
                  <p className="text-gray-600 text-sm">
                    See how many people viewed your ads and track your selling success.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">
                <i className="ri-information-line text-green-600 mr-2"></i>
                Quick & Free
              </h3>
              <p className="text-sm text-gray-700">
                Creating an account is completely free and takes less than a minute. Start buying and selling today!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Reset Password</h3>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetMessage('');
                }}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <div className="mb-4">
              <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <Input
                id="resetEmail"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email"
              />
            </div>

            {resetMessage && (
              <div className={`mb-4 p-3 rounded-lg ${
                resetMessage.includes('sent') 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${
                  resetMessage.includes('sent') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {resetMessage}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                size="md"
                className="flex-1 whitespace-nowrap"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetMessage('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                className="flex-1 whitespace-nowrap"
                onClick={handleForgotPassword}
                disabled={resetLoading}
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
